import type {
  Attempt,
  HumanReviewRequestState,
  HumanReviewResult,
  TaskReadoutState,
} from '@/lib/admin/client';

// The variant union accepted by the shared Badge (src/components/ui/badge.tsx).
export type BadgeVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'success'
  | 'green'
  | 'orange'
  | 'warning'
  | 'error'
  | 'info';

const TASK_READOUT_STATE_VARIANTS: Record<TaskReadoutState, BadgeVariant> = {
  ready: 'secondary',
  running: 'info',
  retrying: 'orange',
  waiting: 'warning',
  blocked: 'error',
  done: 'green',
  delivered: 'success',
};

export function taskReadoutStateBadgeVariant(
  state: TaskReadoutState
): BadgeVariant {
  return TASK_READOUT_STATE_VARIANTS[state];
}

export function attemptBadgeVariant(attempt: Attempt): BadgeVariant {
  if (attempt.state !== 'completed') {
    if (attempt.state === 'leased') return 'success';
    if (attempt.state === 'in_progress') return 'info';
    return 'warning';
  }
  if (attempt.conclusion === 'accepted') return 'green';
  if (attempt.conclusion === 'retry_required') return 'orange';
  return 'secondary';
}

const SUBTLE_STATUS_BACKGROUNDS: Partial<Record<BadgeVariant, string>> = {
  success: 'bg-[#93c5fd]/10 dark:bg-[#93c5fd]/10',
  green: 'bg-emerald-100/50 dark:bg-emerald-950/20',
  orange: 'bg-orange-100/50 dark:bg-orange-950/20',
  warning: 'bg-[#fef9c3]/20 dark:bg-[#fef9c3]/10',
  error: 'bg-destructive/10 dark:bg-destructive/10',
  info: 'bg-[#c4b5fd]/10 dark:bg-[#c4b5fd]/10',
};

export function subtleStatusBadgeBackground(variant: BadgeVariant): string {
  return SUBTLE_STATUS_BACKGROUNDS[variant] ?? '';
}

export function priorityBadgeVariant(priority: string): BadgeVariant {
  const normalized = priority.toUpperCase();
  if (normalized === 'P0') return 'error';
  if (normalized === 'P1') return 'orange';
  if (normalized === 'P2') return 'warning';
  return 'secondary';
}

export function humanReviewStateBadgeVariant(
  state: HumanReviewRequestState
): BadgeVariant {
  if (state === 'approved') return 'green';
  if (state === 'rejected') return 'error';
  if (state === 'changes_requested') return 'orange';
  if (state === 'expired') return 'secondary';
  return 'warning';
}

export function confidenceBadgeVariant(confidence: number): BadgeVariant {
  if (confidence < 0.5) return 'error';
  if (confidence < 0.7) return 'orange';
  if (confidence < 0.85) return 'warning';
  if (confidence < 0.95) return 'green';
  return 'success';
}

export function impactBadgeVariant(impact: string): BadgeVariant {
  const normalized = impact.toLowerCase();
  if (['critical', 'high', 'severe'].includes(normalized)) return 'error';
  if (['medium', 'moderate'].includes(normalized)) return 'orange';
  if (['low', 'minor'].includes(normalized)) return 'green';
  return 'info';
}

export function decisionResultBadgeVariant(
  result: HumanReviewResult
): BadgeVariant {
  if (result === 'approved') return 'green';
  if (result === 'rejected' || result === 'expired') return 'error';
  return 'orange';
}
